import { useState, useEffect } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";
import { FolderOpen, Download, FileText, File, Image } from "lucide-react";
import api from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categories = {
  id_proof: "ID Proof",
  resume: "Resume",
  offer_letter: "Offer Letter",
  contract: "Contract",
  certification: "Certification",
  tax_document: "Tax Document",
  other: "Other",
};

const categoryBadgeColors = {
  id_proof: "bg-blue-50 text-blue-700 border-blue-200",
  resume: "bg-violet-50 text-violet-700 border-violet-200",
  offer_letter: "bg-green-50 text-green-700 border-green-200",
  contract: "bg-amber-50 text-amber-700 border-amber-200",
  certification: "bg-teal-50 text-teal-700 border-teal-200",
  tax_document: "bg-red-50 text-red-700 border-red-200",
  other: "bg-slate-50 text-slate-700 border-slate-200",
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const getFileIcon = (contentType) => {
  if (contentType?.startsWith("image/")) return Image;
  if (contentType?.includes("pdf")) return FileText;
  return File;
};

const MyDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocs();
  }, []);

  const fetchDocs = async () => {
    try {
      const res = await api.get("/self-service/documents");
      setDocuments(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (docId) => {
    const token = localStorage.getItem("token");
    window.open(`${BACKEND_URL}/api/documents/${docId}/download?token=${token}`, "_blank");
  };

  return (
    <div className="space-y-6" data-testid="my-documents-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">My Documents</h1>
        <p className="text-slate-500">View and download your uploaded documents</p>
      </div>

      <Card className="stat-card" style={{ maxWidth: 280 }}>
        <CardContent className="p-0">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-violet-100"><FolderOpen className="h-6 w-6 text-violet-700" /></div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{documents.length}</p>
              <p className="text-sm text-slate-500">Documents</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="card-soft" data-testid="my-docs-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No documents uploaded for you yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">File</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Category</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Size</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400">Uploaded</TableHead>
                  <TableHead className="text-xs uppercase tracking-wide text-slate-400 text-right">Download</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map(doc => {
                  const FileIcon = getFileIcon(doc.content_type);
                  return (
                    <TableRow key={doc.id} className="hover:bg-slate-50/50" data-testid={`my-doc-${doc.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileIcon className="h-4 w-4 text-slate-400" />
                          <span className="font-medium text-slate-800 truncate max-w-[200px]">{doc.filename}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={categoryBadgeColors[doc.category] || categoryBadgeColors.other}>
                          {categories[doc.category] || doc.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{formatFileSize(doc.file_size)}</TableCell>
                      <TableCell className="text-slate-500 text-sm">{new Date(doc.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" className="rounded-full" onClick={() => handleDownload(doc.id)}
                          data-testid={`download-my-doc-${doc.id}`}>
                          <Download className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyDocuments;
